import React, { FC, useEffect, useLayoutEffect, useState } from 'react';
import { addBusinessDays, isWeekend, getHours, isWednesday, isThursday, format, nextSaturday } from 'date-fns'
import "react-datepicker/dist/react-datepicker.css";
import DeliveryOptions from "./business/DeliveryOptions";
import RentalPoint from "./business/RentalPoint";
import { AvailabilityInterface, StepProps } from "../types/types";
import { useTranslation } from "react-i18next";
import BookingService from "../api/BookingService";
import LoadingIndicator from "./utils/LoadingIndicator";
import {beneluxCountries, getBookingConfig, getRentalPointDelivery, logError} from "../data/price";
import PlayRangePicker from './business/PlayRangePicker';
import { setActivePromotion, setPlayRange } from '../stateReducers/deliverySlice';
import { addDays } from './utils/stateHelper';
import { useAppDispatch, useAppSelector } from '../hooks';
import { isInPromoInterval } from '../api/PromotionService';
import PromoOptions from './business/PromoOptions';

const Step2: FC<StepProps> = ({setStepValidity}) => {
    const d = useAppDispatch();
    const playStart = useAppSelector((state) => {
        return state.delivery.playStart || null;
    });
    const playEnd = useAppSelector((state) => {
        return state.delivery.playEnd || null;
    });
    const deliveryOptions = useAppSelector((state) => {
        return state.delivery;
    });
    const lang = useAppSelector((state) => { return state.globals.lang; });
    const formType = useAppSelector((state) => { return state.delivery.formType; });
    const maxStepReached = useAppSelector((state) => { return state.globals.maxStepReached; });
    const {t} = useTranslation();
    const [availabilityLoading, setAvailabilityLoading] = useState(false);
    const [availabilityData, setRawAvailabilityData] = useState<AvailabilityInterface>({} as AvailabilityInterface);
    const freeDeliverySwitch = useAppSelector((state) => { return state.extraItems.freeDeliverySwitch; });

    // Wrapper for the raw setter so that only the most recent data is set
    function setAvailabilityData(data: AvailabilityInterface) {
        if ('clickedDate' in Object.keys(availabilityData)) {
            if (availabilityData['clickedDate'] !== format(playStart!, "yyyy-MM-dd")) {
                console.log("Invalid clickedDate");
                return;
            }
        }
        setRawAvailabilityData(data);
    }

    function calcMinDate() {
        return addDays(deliveryOptions.minPlayStart!, 1);
    }

    const config = getBookingConfig();

    // Earliest play date is 'tomorrow' or the day after if it's past a certain time
    const [minDate, setMinDate] = useState<Date | null>(calcMinDate());

    // Meant to only fire once per page load
    useEffect(() => {
        if (maxStepReached === 2) {
            const playStart = nextSaturday(minDate!);
            d(setPlayRange({playStart: playStart, range: 1}));
        }
    }, [minDate]);


    function isValidPlayStartAndDeliveryType() {
        if (playStart) {
            if (deliveryOptions.deliveryType) {
                if (deliveryOptions.deliveryType === 'rental') {
                    return !!deliveryOptions.rentalPoint;
                } else {
                    return true;
                }
            } else {
                return false;
            }
        } else {
            return false;
        }
    }
    useEffect(() => {
        let isValid: boolean;
        if (maxStepReached === 2) {
            isValid = false;
        }
        isValid = isValidPlayStartAndDeliveryType();
        console.log('checked validity step 2', isValid);
        setStepValidity(isValid);
    }, [playStart, deliveryOptions.deliveryType]);

    const gunOptions = useAppSelector((state) => { return state.gunOptions; });
    useEffect(() => {
        if (playStart === null) {
            return;
        }
        if (gunOptions.gunType === null) {
            return;
        }
        if (gunOptions.quantity === null) {
            return;
        }

        // Because PromoOptions.tsx stops being loaded outside the promo dates, this must be
        // handled here
        if (!isInPromoInterval(playStart, lang, formType)) {
            d(setActivePromotion(""));
        }

        setAvailabilityLoading(true);
        BookingService.checkAllAvailability(gunOptions.gunType, playStart!, playEnd!, gunOptions.quantity)
            .then((data: any) => {
                setAvailabilityData(data);
                setAvailabilityLoading(false);
            }).catch((error) => {
            console.error(error);
        });
    }, [
        // Specifically use playEnd rather than playStart because playStart will
        // change before playEnd has been properly calculated
        playEnd,
        gunOptions.gunType,
        gunOptions.quantity,
        deliveryOptions.activePromo
    ]);

    const userInBenelux = config.country && beneluxCountries.includes(config.country);
    const [wantsToSeeRentalPoints, setWantsToSeeRentalPoints] = useState<boolean>(false);

    const displayDeliveryOptions = !freeDeliverySwitch;

    const displayRentalPoints = userInBenelux || wantsToSeeRentalPoints;

    return (
        <div className="">
            <p>{t('step2Directions')}</p>
            <div className="datepicker">
                {formType === 'YouthPromo' &&
                    <div>
                        {t('choosePlayRangeYouthPromo')}
                    </div>
                }
                {!deliveryOptions.playStart && formType !== 'YouthPromo' &&
                    <div className="pt-2">{t('selectPlaydate')}</div>
                }
                <PlayRangePicker minDate={minDate!} />
            </div>
            {playStart && isInPromoInterval(playStart, lang, formType) && userInBenelux &&
                <PromoOptions />
            }
            <div>
                {playStart && <div className="pt-2">{t('deliveryOptionsTitle')}</div>}
                {playStart && availabilityLoading &&
                    <LoadingIndicator text={t('loadingRentalPointsLabel')}/>}
                {playStart && !availabilityLoading && displayDeliveryOptions &&
                    <DeliveryOptions availability={availabilityData} />}
                {deliveryOptions.playStart && !availabilityLoading && displayRentalPoints &&
                    <RentalPoint availability={availabilityData} />}
                {deliveryOptions.playStart && !availabilityLoading && !displayRentalPoints && <>
                    <br />
                    <span className='clickable-aside' onClick={() => {setWantsToSeeRentalPoints(true)}}>{t('wantToSeeRentalPoints')}</span>
                </>}
            </div>
        </div>
    );
};

export default Step2;
